import React from 'react';
import { Calendar, MapPin, Users } from 'lucide-react';
import { EditableText } from './inline/EditableText';

// Default content (fallback if CMS data not available)
const defaultContent = {
  title: "World Nuclear Congress",
  description: "The World Nuclear Congress brings together industry leaders, policymakers, scientists, and innovators from around the world to discuss the future of nuclear energy, sustainability, and global energy security.",
  tagline: "\"Working together today for a more secure and prosperous tomorrow.\"",
  stats: [
    { value: "50+", label: "Speakers & Presenters" },
    { value: "8+", label: "High Level Congressional & Allied Leadership Sessions" },
    { value: "500", label: "Limited Spots" },
    { value: "7+", label: "Countries" }
  ]
};

const InfoSection = ({ content, sectionId }) => {
  // Merge CMS content with defaults
  const data = { ...defaultContent, ...content };
  const stats = data.stats || defaultContent.stats;
  const scope = (path) => ({ type: 'homepage_section', sectionId, path });
  const eventDateDisplay = data.event_date_display || 'Coming January 2027';
  const eventDateDetail = data.event_date_detail || 'Join us for the opening reception followed by a full day of curated programs, meaningful engagement and innovation.';
  const locationAddress = data.location_address || 'University of Tennessee – 1502 Cumberland Avenue, Knoxville, TN 37916';
  const locationDetail = data.location_detail || 'Student Union Building. Hosted by CEE at the University of Tennessee, Knoxville.';
  const whoAttendsSubtitle = data.who_attends_subtitle || 'Global and State Leaders';
  const whoAttendsDetail = data.who_attends_detail || 'Senior leaders, government officials, allied nations and embassy representatives, industry executives, U.S. National Laboratories, developers, educational institutions and more.';

  return (
    <section id="info-section" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Statement */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#00457f] mb-6">
            <EditableText as="span" scope={scope('title')} value={data.title} multiline={false}>{data.title}</EditableText>
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto font-light">
            <EditableText as="span" scope={scope('description')} value={data.description}>{data.description}</EditableText>
          </p>
          {data.tagline && (
            <p className="text-2xl md:text-3xl font-bold text-[#00457f] mt-6">
              <EditableText as="span" scope={scope('tagline')} value={data.tagline}>{data.tagline}</EditableText>
            </p>
          )}
        </div>

        {/* Event Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-[#00457f] hover:shadow-2xl transition-shadow">
            <Calendar className="w-12 h-12 text-[#00457f] mb-4" />
            <h3 className="text-xl font-bold text-[#00457f] mb-3">Event Dates</h3>
            <p className="text-lg text-gray-700 font-semibold mb-2"><EditableText as="span" scope={scope('event_date_display')} value={data.event_date_display} multiline={false}>{data.event_date_display || 'Coming January 2027'}</EditableText></p>
            <p className="text-gray-600"><EditableText as="span" scope={scope('event_date_detail')} value={data.event_date_detail}>{data.event_date_detail || 'Join us for the opening reception followed by a full day of curated programs, meaningful engagement and innovation.'}</EditableText></p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-yellow-500 hover:shadow-2xl transition-shadow">
            <MapPin className="w-12 h-12 text-yellow-600 mb-4" />
            <h3 className="text-xl font-bold text-[#00457f] mb-3">Location</h3>
            <p className="text-lg text-gray-700 font-semibold mb-2"><EditableText as="span" scope={scope('location_address')} value={data.location_address}>{data.location_address || 'University of Tennessee – 1502 Cumberland Avenue, Knoxville, TN 37916'}</EditableText></p>
            <p className="text-gray-600"><EditableText as="span" scope={scope('location_detail')} value={data.location_detail}>{data.location_detail || 'Student Union Building. Hosted by CEE at the University of Tennessee, Knoxville.'}</EditableText></p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-[#00457f] hover:shadow-2xl transition-shadow">
            <Users className="w-12 h-12 text-[#00457f] mb-4" />
            <h3 className="text-xl font-bold text-[#00457f] mb-3">Who Attends</h3>
            <p className="text-lg text-gray-700 font-semibold mb-2"><EditableText as="span" scope={scope('who_attends_subtitle')} value={data.who_attends_subtitle} multiline={false}>{data.who_attends_subtitle || 'Global and State Leaders'}</EditableText></p>
            <p className="text-gray-600"><EditableText as="span" scope={scope('who_attends_detail')} value={data.who_attends_detail}>{data.who_attends_detail || 'Senior leaders, government officials, allied nations and embassy representatives, industry executives, U.S. National Laboratories, developers, educational institutions and more.'}</EditableText></p>
          </div>
        </div>

        {/* What to Expect */}
        <div className="bg-[#00457f] rounded-3xl p-10 md:p-12 text-white shadow-2xl">
          <h3 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            What to Expect
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <p className="text-4xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-transparent bg-clip-text mb-2">
                  {stat.value}
                </p>
                <p className="text-lg">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InfoSection;